/** @format */

"use client";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "./utils/cn";

const BUSINESS_PHONE = "+1 (281) 972-8100";
const BUSINESS_PHONE_HREF = "tel:+12819728100";

const NAV_LINKS = [
    { label: "Features", target: "features" },
    { label: "Integration", target: "integrations" },
    { label: "Pricing", path: "/business/pricing" },
] as const;

const PhoneIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
);

export function BusinessNavbar({ className }: { className?: string }) {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToSection = (id: string) => {
        setMobileOpen(false);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Section lives on the landing page — go there and jump to it.
            navigate(`/business#${id}`);
        }
    };

    const handleNavClick = (link: (typeof NAV_LINKS)[number]) => {
        if ("path" in link && link.path) {
            setMobileOpen(false);
            navigate(link.path);
        } else if ("target" in link) {
            scrollToSection(link.target);
        }
    };

    const goToSignIn = () => {
        setMobileOpen(false);
        navigate("/collect/sign-in");
    };

    const goToSignUp = () => {
        setMobileOpen(false);
        navigate("/collect/sign-up");
    };

    return (
        <header
            className={cn(
                "fixed top-0 inset-x-0 z-50 transition-all duration-300",
                scrolled
                    ? "bg-[#070e29]/90 backdrop-blur-md border-b border-white/10 shadow-[0_8px_30px_rgba(7,14,41,0.45)]"
                    : "bg-transparent border-b border-transparent",
                className
            )}
        >
            <div className="mx-auto max-w-7xl px-4 md:px-8">
                <div className="flex h-16 md:h-[72px] items-center justify-between">
                    {/* Brand */}
                    <Link
                        to="/business"
                        className="flex items-center gap-2.5 group"
                        onClick={() => setMobileOpen(false)}
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#ffd02e] to-[#ffaa00] shadow-[0_6px_18px_rgba(255,176,0,0.4)]">
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#0d1636"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                        </span>
                        <span className="flex flex-col leading-none">
                            <span className="text-white font-extrabold text-[1.02rem] tracking-tight">
                                FundsyFi
                            </span>
                            <span className="text-[#ffcc00] text-[0.62rem] font-semibold tracking-[0.22em] uppercase">
                                Recover
                            </span>
                        </span>
                    </Link>

                    {/* Desktop nav links */}
                    <nav className="hidden md:flex items-center gap-8">
                        {NAV_LINKS.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link)}
                                className="relative text-[0.95rem] font-medium text-white/80 hover:text-white transition-colors duration-200 cursor-pointer after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-gradient-to-r after:from-[#ffd02e] after:to-[#ffaa00] after:transition-all after:duration-300 hover:after:w-full"
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Desktop actions */}
                    <div className="hidden md:flex items-center gap-5">
                        <a
                            href={BUSINESS_PHONE_HREF}
                            className="flex items-center gap-2 text-white/85 hover:text-white transition-colors duration-200 group"
                        >
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 border border-white/15 group-hover:bg-[#1ec195]/20 group-hover:border-[#1ec195]/40 transition-colors duration-200">
                                <PhoneIcon className="text-[#1ec195]" />
                            </span>
                            <span className="flex flex-col leading-tight">
                                <span className="text-[0.62rem] uppercase tracking-wider text-white/45 font-semibold">
                                    Give us a call
                                </span>
                                <span className="text-[0.9rem] font-semibold tracking-tight">
                                    {BUSINESS_PHONE}
                                </span>
                            </span>
                        </a>

                        <button
                            onClick={goToSignIn}
                            className="text-[0.92rem] font-semibold text-white/80 hover:text-white transition-colors duration-200 cursor-pointer"
                        >
                            Sign in
                        </button>

                        <button
                            onClick={goToSignUp}
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-[#ffd02e] to-[#ffaa00] px-5 py-2.5 text-[0.92rem] font-bold text-[#0d1636] shadow-[0_10px_28px_rgba(255,176,0,0.42)] hover:shadow-[0_14px_36px_rgba(255,176,0,0.55)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                        >
                            Start free
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile toggle */}
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        className="md:hidden flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white"
                        aria-label="Toggle menu"
                        aria-expanded={mobileOpen}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            {mobileOpen ? (
                                <motion.svg
                                    key="close"
                                    initial={{ rotate: -90, opacity: 0 }}
                                    animate={{ rotate: 0, opacity: 1 }}
                                    exit={{ rotate: 90, opacity: 0 }}
                                    transition={{ duration: 0.18 }}
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </motion.svg>
                            ) : (
                                <motion.svg
                                    key="open"
                                    initial={{ rotate: 90, opacity: 0 }}
                                    animate={{ rotate: 0, opacity: 1 }}
                                    exit={{ rotate: -90, opacity: 0 }}
                                    transition={{ duration: 0.18 }}
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="3" y1="18" x2="21" y2="18" />
                                </motion.svg>
                            )}
                        </AnimatePresence>
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="md:hidden overflow-hidden bg-[#070e29]/97 backdrop-blur-md border-b border-white/10"
                    >
                        <div className="px-5 py-5 flex flex-col gap-1">
                            {NAV_LINKS.map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link)}
                                    className="text-left text-white/85 hover:text-white text-base font-medium py-3 px-2 rounded-lg hover:bg-white/[0.06] transition-colors duration-150"
                                >
                                    {link.label}
                                </button>
                            ))}

                            <a
                                href={BUSINESS_PHONE_HREF}
                                className="flex items-center gap-3 mt-2 py-3 px-2 text-white/90"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1ec195]/15 border border-[#1ec195]/30">
                                    <PhoneIcon className="text-[#1ec195]" />
                                </span>
                                <span className="flex flex-col leading-tight">
                                    <span className="text-[0.62rem] uppercase tracking-wider text-white/45 font-semibold">
                                        Talk to sales
                                    </span>
                                    <span className="text-[0.95rem] font-semibold">
                                        {BUSINESS_PHONE}
                                    </span>
                                </span>
                            </a>

                            <button
                                onClick={goToSignIn}
                                className="mt-3 inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-[0.95rem] font-semibold text-white/90 hover:bg-white/10 transition-colors duration-150"
                            >
                                Sign in
                            </button>

                            <button
                                onClick={goToSignUp}
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-[#ffd02e] to-[#ffaa00] px-5 py-3 text-[0.95rem] font-bold text-[#0d1636] shadow-[0_10px_28px_rgba(255,176,0,0.42)]"
                            >
                                Start free
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}

export default BusinessNavbar;
